/**
 * HR Manager Permission System
 *
 * This module defines which features each HR Manager type can access.
 * It mirrors the backend permission system for consistency.
 */

// HRManagerType is already defined in services/hr-manager.service.ts
type HRManagerType = 'PAYROLL' | 'OPERATIONS' | 'ONBOARDING' | 'ONBOARDING_ASSISTANT' | 'QUALITY_CONTROL';

export enum HRFeature {
  SALES_REPORTS = 'SALES_REPORTS',
  PAY_REPORTS = 'PAY_REPORTS',
  AGENT_ASSIGNMENT = 'AGENT_ASSIGNMENT',
  COMMISSION_RATES = 'COMMISSION_RATES',
  OVERRIDE_RATES = 'OVERRIDE_RATES',
  AGENT_STATUS = 'AGENT_STATUS',
  ORGANIZATIONS = 'ORGANIZATIONS',
  QUALITY_CONTROL = 'QUALITY_CONTROL',
}

/**
 * Permission matrix: Maps HR Manager types to their allowed features
 */
const HR_PERMISSIONS: Record<HRManagerType, HRFeature[]> = {
  // PAYROLL: Full access to all features
  PAYROLL: [
    HRFeature.SALES_REPORTS,
    HRFeature.PAY_REPORTS,
    HRFeature.AGENT_ASSIGNMENT,
    HRFeature.COMMISSION_RATES,
    HRFeature.OVERRIDE_RATES,
    HRFeature.AGENT_STATUS,
    HRFeature.ORGANIZATIONS,
    HRFeature.QUALITY_CONTROL,
  ],

  // OPERATIONS: All features except pay reports and organizations
  OPERATIONS: [
    HRFeature.SALES_REPORTS,
    HRFeature.AGENT_ASSIGNMENT,
    HRFeature.COMMISSION_RATES,
    HRFeature.OVERRIDE_RATES,
    HRFeature.AGENT_STATUS,
    HRFeature.QUALITY_CONTROL,
  ],

  // ONBOARDING: Agent assignment and status only (includes organizations to view teams)
  ONBOARDING: [
    HRFeature.AGENT_ASSIGNMENT,
    HRFeature.AGENT_STATUS,
    HRFeature.ORGANIZATIONS, // Need to view organizations to assign agents to teams
  ],

  // ONBOARDING_ASSISTANT: Agent status only (read-only operations)
  ONBOARDING_ASSISTANT: [
    HRFeature.AGENT_STATUS,
  ],

  // QUALITY_CONTROL: Quality control features only
  QUALITY_CONTROL: [
    HRFeature.QUALITY_CONTROL,
  ],
};

/**
 * Check if an HR Manager has permission to access a specific feature
 *
 * @param hrManagerType - The type of HR Manager (null means no access)
 * @param feature - The feature to check access for
 * @returns true if the HR Manager has access, false otherwise
 */
export function checkHRPermission(
  hrManagerType: HRManagerType | null | undefined,
  feature: HRFeature
): boolean {
  if (!hrManagerType) {
    return false;
  }

  const allowedFeatures = HR_PERMISSIONS[hrManagerType];
  return allowedFeatures ? allowedFeatures.includes(feature) : false;
}

/**
 * Get all features that an HR Manager type can access
 *
 * @param hrManagerType - The type of HR Manager
 * @returns Array of features the HR Manager can access
 */
export function getHRPermissions(hrManagerType: HRManagerType | null | undefined): HRFeature[] {
  if (!hrManagerType) {
    return [];
  }

  return HR_PERMISSIONS[hrManagerType] || [];
}

/**
 * Map navigation tab IDs to HR features
 */
export const TAB_TO_FEATURE_MAP: Record<string, HRFeature> = {
  'sales-reports': HRFeature.SALES_REPORTS,
  'pay-reports': HRFeature.PAY_REPORTS,
  'agent-assignment': HRFeature.AGENT_ASSIGNMENT,
  'commission-rates': HRFeature.COMMISSION_RATES,
  'override-rates': HRFeature.OVERRIDE_RATES,
  'agent-status': HRFeature.AGENT_STATUS,
  'organizations': HRFeature.ORGANIZATIONS,
  'quality-control': HRFeature.QUALITY_CONTROL,
};
